import type { BuilderScreen, BuilderSheet, Food, Id, MacroKey, Pct, Plan, SaveTplMode, Targets, Tip } from '../types';
import { endTimeAfter, SLOTS } from '../lib/plan';
import { clampKcal, mealTargets, ratioFromOptions } from '../lib/nutrition';

export interface TargetEdit { mi: number; v: Record<'k' | MacroKey, string> }

export interface BuilderState {
  screen: BuilderScreen;
  sheet: BuilderSheet;
  patientId: Id | null;
  plan: Plan | null;
  savedPlan: Plan | null;       // last version acknowledged by backend (for dirty check)
  tplFilter: string;
  mi: number; oi: number;       // selected meal / option for sheets
  foodQuery: string;
  tipEditing: number | null;
  splitOpen: number | null;
  targetEdit: TargetEdit | null;
  saveTplMode: SaveTplMode;
  newTplName: string;
  tplName: string;
  shareWa: boolean; confirm: boolean; reason: string;
}

export const initialState = (patientId: Id | null): BuilderState => ({
  screen: 'tpl', sheet: null, patientId, plan: null, savedPlan: null, tplFilter: 'All',
  mi: 0, oi: 0, foodQuery: '', tipEditing: null, splitOpen: null, targetEdit: null, saveTplMode: 'none', newTplName: '', tplName: '', shareWa: false, confirm: false, reason: '',
});

export type Action =
  | { type: 'pickPatient'; id: Id; tplFilter: string }
  | { type: 'go'; screen: BuilderScreen }
  | { type: 'sheet'; sheet: BuilderSheet; mi?: number; oi?: number }
  | { type: 'startPlan'; plan: Plan; tplName: string; screen?: BuilderScreen; sheet?: BuilderSheet }
  | { type: 'discard' }
  | { type: 'markSaved'; id?: Id }
  | { type: 'setFilter'; v: string }
  | { type: 'plan'; fn: (p: Plan) => void }          // immutable-by-clone mutation helper
  | { type: 'pct'; key: MacroKey; value: number }
  | { type: 'preset'; pct: Pct; label: string }
  | { type: 'kcal'; delta: number }
  | { type: 'split'; mode: 'template' | 'standard'; tplShares?: number[]; tplRatios?: (Pct | null)[]; tplTimes?: { start: string; end: string }[] }
  | { type: 'scaleShares' }
  | { type: 'select'; mi?: number; oi?: number }
  | { type: 'addOption'; mi: number; openSearch?: boolean }
  | { type: 'removeOption' }
  | { type: 'addFood'; food: Food; inline?: boolean }   // inline: add from the in-page search, leave sheets alone
  | { type: 'foodQty'; fi: number; qty: number }
  | { type: 'removeFood'; fi: number }
  | { type: 'tip'; op: 'add' | 'remove' | 'edit' | 'done'; ti?: number; patch?: Partial<Tip> }
  | { type: 'foodQuery'; v: string }
  | { type: 'splitOpen'; i: number | null }
  | { type: 'targetEdit'; edit: TargetEdit | null }
  | { type: 'targetSave' }
  | { type: 'targetReset'; mi: number }
  | { type: 'targetFromOptions' }
  | { type: 'assignField'; patch: Partial<Pick<BuilderState, 'saveTplMode' | 'newTplName' | 'tplName' | 'shareWa' | 'confirm' | 'reason'>> };

const clone = <T,>(x: T): T => JSON.parse(JSON.stringify(x));
const withPlan = (s: BuilderState, fn: (p: Plan) => void): BuilderState => { if (!s.plan) return s; const plan = clone(s.plan); fn(plan); return { ...s, plan }; };

export function builderReducer(s: BuilderState, a: Action): BuilderState {
  switch (a.type) {
    case 'pickPatient': return { ...s, patientId: a.id, screen: 'plans', tplFilter: a.tplFilter };
    case 'go': return { ...s, screen: a.screen, sheet: null };
    case 'sheet': return { ...s, sheet: a.sheet, mi: a.mi ?? s.mi, oi: a.oi ?? s.oi, foodQuery: '', tipEditing: null, splitOpen: null, targetEdit: null, ...(a.sheet === 'assign' || a.sheet === 'previewAssign' ? { confirm: false, reason: '' } : {}), ...(a.sheet === 'previewAssign' ? { saveTplMode: 'none' as const, newTplName: '' } : {}) };
    case 'startPlan': return { ...s, screen: a.screen ?? 'goals', plan: a.plan, savedPlan: null, saveTplMode: 'none', newTplName: '', tplName: a.tplName, sheet: a.sheet ?? null, confirm: false, reason: '' };
    case 'discard': return { ...s, screen: 'tpl', plan: null, savedPlan: null, sheet: null };
    case 'markSaved': return s.plan ? { ...s, plan: a.id != null ? { ...s.plan, id: a.id } : s.plan, savedPlan: a.id != null ? { ...s.plan, id: a.id } : s.plan } : s;
    case 'setFilter': return { ...s, tplFilter: a.v };
    case 'plan': return withPlan(s, a.fn);
    case 'pct': return withPlan(s, p => { p.pct = { ...p.pct, [a.key]: Math.max(5, Math.min(70, Math.round(a.value) || 0)) }; p.macroPreset = null; });
    case 'preset': return withPlan(s, p => { p.pct = { ...a.pct }; p.macroPreset = a.label; });
    case 'kcal': return withPlan(s, p => { p.kcal = clampKcal(p.kcal + a.delta); });
    case 'split': return withPlan(s, p => {
      p.split = a.mode;
      p.meals.forEach((m, i) => {
        if (a.mode === 'template' && a.tplShares) {
          m.share = a.tplShares[i] ?? 0;
          m.ratio = a.tplRatios?.[i] ?? null;
          const times = a.tplTimes?.[i];
          if (times) {
            m.time = times.start;
            m.endTime = times.end;
          }
        } else if (a.mode === 'standard') {
          const slot = SLOTS[i];
          if (!slot) return;
          m.skip = false;
          m.share = slot.share;
          m.time = slot.time;
          m.endTime = endTimeAfter(slot.time);
          m.ratio = null;
        }
      });
    });
    case 'scaleShares': return withPlan(s, p => {
      const total = p.meals.reduce((sum, m) => sum + (m.skip ? 0 : (+m.share || 0)), 0);
      if (!total) return;
      p.meals.forEach(m => {
        if (m.skip) return;
        m.share = Math.round(((+m.share || 0) * 100 / total) * 100) / 100;
      });
    });
    case 'select': return { ...s, mi: a.mi ?? s.mi, oi: a.oi ?? s.oi };
    case 'addOption': {
      const n = withPlan(s, p => { p.meals[a.mi].options.push({ foods: [], tips: [] }); });
      const oi = n.plan!.meals[a.mi].options.length - 1;
      if (a.openSearch === false) return { ...n, mi: a.mi, oi };
      return { ...n, sheet: 'search', mi: a.mi, oi, foodQuery: '' };
    }
    case 'removeOption': {
      const n = withPlan(s, p => { const o = p.meals[s.mi].options; o.splice(s.oi, 1); if (!o.length) o.push({ foods: [], tips: [] }); });
      const len = n.plan!.meals[s.mi].options.length;
      return { ...n, sheet: null, oi: Math.min(s.oi, len - 1) };
    }
    case 'addFood': {
      const n = withPlan(s, p => { p.meals[s.mi].options[s.oi].foods.push({ ...a.food }); });
      return a.inline ? { ...n, foodQuery: '' } : { ...n, sheet: 'opt', foodQuery: '' };
    }
    case 'foodQty': return withPlan(s, p => { p.meals[s.mi].options[s.oi].foods[a.fi].qty = Math.max(0.25, Math.round(a.qty * 100) / 100); });
    case 'removeFood': return withPlan(s, p => { p.meals[s.mi].options[s.oi].foods.splice(a.fi, 1); });
    case 'tip': {
      if (a.op === 'edit') return { ...s, tipEditing: a.ti ?? null };
      if (a.op === 'done') return { ...s, tipEditing: null };
      if (a.op === 'add') { const n = withPlan(s, p => { p.meals[s.mi].options[s.oi].tips.push({ heading: '', detail: '' }); }); return { ...n, tipEditing: n.plan!.meals[s.mi].options[s.oi].tips.length - 1 }; }
      if (a.op === 'remove') return { ...withPlan(s, p => { p.meals[s.mi].options[s.oi].tips.splice(a.ti!, 1); }), tipEditing: null };
      return s;
    }
    case 'foodQuery': return { ...s, foodQuery: a.v };
    case 'splitOpen': return { ...s, splitOpen: a.i };
    case 'targetEdit': return { ...s, targetEdit: a.edit };
    case 'targetSave': {
      const ed = s.targetEdit; if (!ed || !s.plan) return s;
      const m = s.plan.meals[ed.mi]; const cur = mealTargets(s.plan, m);
      const num = (x: string) => Math.max(0, parseFloat(x) || 0);
      const n = withPlan(s, p => {
        const mm = p.meals[ed.mi]; mm.ov = { ...mm.ov };
        (['k', 'p', 'c', 'f'] as const).forEach(k => { const v = num(ed.v[k]); if (Math.abs(v - (k === 'k' ? Math.round(cur.k) : Math.round(cur[k] * 10) / 10)) < 1e-6) return; if (k === 'k') mm.share = p.kcal ? Math.round((v / p.kcal) * 10000) / 100 : 0; else mm.ov[k] = v; });
      });
      return { ...n, targetEdit: null };
    }
    case 'targetReset': return withPlan(s, p => { p.meals[a.mi].ov = {}; });
    case 'targetFromOptions': {
      if (!s.plan) return s; const r = ratioFromOptions(s.plan.meals[s.mi].options); if (!r) return s;
      return { ...withPlan(s, p => { const mm = p.meals[s.mi]; mm.share = p.kcal ? Math.round((r.k / p.kcal) * 10000) / 100 : 0; mm.ratio = { p: r.p, c: r.c, f: r.f }; mm.ov = {}; }), targetEdit: null };
    }
    case 'assignField': return { ...s, ...a.patch };
    default: return s;
  }
}

export const editFromTargets = (mi: number, T: Targets): TargetEdit => ({ mi, v: { k: String(Math.round(T.k)), p: String(Math.round(T.p * 10) / 10), c: String(Math.round(T.c * 10) / 10), f: String(Math.round(T.f * 10) / 10) } });
